import StandardLayout from '@/common/layouts/StandardLayout';
import { Row, Col, Input, FormGroup, Label, InputGroup, InputGroupText, Button } from 'reactstrap';
import { useSelector } from 'react-redux';
import { ReactComponent as TetherIcon } from '@/assets/tokens/tether-green.svg';
import { useBalance } from 'wagmi';
import TokenBalance from '@/components/TokenBalance';
import { IoIosSwap } from 'react-icons/io';
import { config } from '@repo/common-types';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '@/redux/root';
import { AppState } from '@/redux/root';
import { setFromWallet, setToWallet, setAmount } from './wallet-transfers-slice';
import { useWalletTransferMutation } from '@/redux/api/transactions';

const WalletTransfersPage = () => {
  const dispatch = useDispatch<AppDispatch>();

  const [handleWalletTransfer] = useWalletTransferMutation();

  const availableWallets = [
    {
      name: 'Pool',
      address: config.contracts.poolAccount,
    },
    {
      name: 'Tournament',
      address: config.contracts.tournaments,
    },
    {
      name: 'Leaderboard',
      address: config.contracts.leaderboard,
    },
    {
      name: 'Smart Account',
      address: config.contracts.smartWallet,
    },
    {
      name: 'Ticket',
      address: config.contracts.ticket,
    },
  ];

  const { fromWallet, toWallet, amount } = useSelector((state: AppState) => state.walletTransfers);

  const { data: fromBalance, isLoading: fromBalanceLoading } = useBalance({
    address: fromWallet as `0x${string}`,
    token: config.contracts.usdt,
  });

  const { data: toBalance, isLoading: toBalanceLoading } = useBalance({
    address: toWallet as `0x${string}`,
    token: config.contracts.usdt,
  });

  const swapWallets = () => {
    dispatch(setFromWallet(toWallet));
    dispatch(setToWallet(fromWallet));
  };

  const handleTransfer = async () => {
    handleWalletTransfer({
      amount: Number(amount),
      from: fromWallet!,
      to: toWallet!,
      meta: {
        toast: {
          success: 'Transfer successful',
          error: 'Transfer failed',
        },
      },
    });
  };

  return (
    <StandardLayout pageTitle="Transfer to wallet">
      <Row className="gap-4 p-4">
        <Col>
          <Row>
            <h6 className="p-0">From:</h6>
          </Row>
          <Row>
            <select
              value={fromWallet ?? ''}
              onChange={(e) => dispatch(setFromWallet(e.target.value))}
              className="form-select"
            >
              <option value="">{`-`}</option>
              {availableWallets.map((wallet) => (
                <option key={wallet.address} value={wallet.address}>
                  {wallet.name}
                </option>
              ))}
            </select>
          </Row>
          {fromWallet && (
            <Row className="py-4">
              <p className="p-0">{fromWallet}</p>
              <TokenBalance
                balance={fromBalance?.value}
                tokenSymbol="USDT"
                tokenDecimals={fromBalance?.decimals}
                formatDecimals={2}
                isLoading={fromBalanceLoading}
                containerClasses="p-0"
              />
            </Row>
          )}
        </Col>
        <Col className="d-flex justify-content-center align-items-center">
          <IoIosSwap className="cursor-pointer" onClick={swapWallets} size={40} />
        </Col>
        <Col>
          <Row>
            <h6 className="p-0">To:</h6>
          </Row>
          <Row>
            <select
              value={toWallet ?? ''}
              onChange={(e) => dispatch(setToWallet(e.target.value))}
              className="form-select"
            >
              <option value="">{`-`}</option>
              {availableWallets.map((wallet) => (
                <option disabled={wallet.address === fromWallet} key={wallet.address} value={wallet.address}>
                  {wallet.name}
                </option>
              ))}
            </select>
          </Row>
          {toWallet && (
            <Row className="py-4">
              <p className="p-0">{toWallet}</p>
              <TokenBalance
                balance={toBalance?.value}
                tokenSymbol="USDT"
                tokenDecimals={toBalance?.decimals}
                formatDecimals={2}
                isLoading={toBalanceLoading}
                containerClasses="p-0"
              />
            </Row>
          )}
        </Col>
      </Row>
      <Row>
        {toWallet && fromWallet && (
          <Col>
            <Row>
              <Col lg={3}>
                <FormGroup>
                  <Label>Amount</Label>
                  <InputGroup>
                    <InputGroupText>
                      <TetherIcon />
                    </InputGroupText>
                    <Input value={amount!} onChange={(e) => dispatch(setAmount(e.target.value))} type="number" />
                  </InputGroup>
                </FormGroup>
                <Button onClick={handleTransfer} color="primary">
                  Submit
                </Button>
              </Col>
            </Row>
          </Col>
        )}
      </Row>
    </StandardLayout>
  );
};

export default WalletTransfersPage;
